import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppConfiguration } from 'config';
import {
  Alert,
  Card, CardBody, Col, Container, Form, FormFeedback, FormGroup, Input, Label, Row,
} from 'reactstrap';
import { useFormik } from 'formik';
import { us_states } from 'common/data/us_states';
import * as Yup from 'yup';
import * as CONSTANTS from 'common/utils/data_validations';
import { MetaTags } from 'react-meta-tags';
import {
  Link, Prompt, useHistory, useLocation,
} from 'react-router-dom';
import { add_funds_options_billing, cardDigits, animatedlogo, REQUIRED_FEATURE_FLAGS, EMAIL_HAS_ALREADY_BEEN_TAKEN } from 'helpers/sf_constants';
import { AppContext, SfDollarStringFormatter, getPrimaryButtonClass, getPrimaryTextClass } from 'helpers/sf_utils';
import NumberFormat from 'react-number-format';
import { registerUser, registerUserFailed } from 'store/actions';
import ToastAlert, { ToastAlertIconType } from 'components/Common/ToastAlert/ToastAlert';
import creditCardImage from "assets/images/icons/credit-card.svg"
import { LATEST } from 'common/sf_strings';
import useFeatureFlags from 'hooks/useFeatureFlags';
import { useMemo } from 'react';
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import useRegistratonPage from 'hooks/useRegistratonPage';
import { convenienceFeeNote } from 'helpers/sf_billing_helper';

function BillingRegistration(props) {
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isUpdating, setIsUpdating] = useState(true);
  const [selectImageCreditCard, setSelectImageCreditCard] = useState(creditCardImage);
  const [checkUserInfo, setCheckUserInfo] = useState(false)
  const [isFeatureFlagActive] = useFeatureFlags();
  const stripe = useStripe();
  const elements = useElements();
  const [cardNumberValid, setCardNumberValid] = useState(false)
  const [cardCVVValid, setCardCVVValid] = useState(false)
  const [cardExpiryValid, setCardExpiryValid] = useState(false)
  const [is_admin_adding_client, admin_url_param, registration_pending_user_url_param] = useRegistratonPage()

  const use_new_stripe_setup = useMemo(() => (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_USE_NEW_PAYMENT_SETUP)), [isFeatureFlagActive])
  const registration_page_enhancements = useMemo(() => (isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_REGISTRATION_PAGE_ENHANCEMENTS)), [isFeatureFlagActive])

  const CREDIT_AMOUNT_OPTION = {
    label: '$250',
    value: 250,
  };

  const [selectedCreditAmount, setSelectedCreditAmount] = useState(CREDIT_AMOUNT_OPTION);
  const { showExitPrompt, setShowExitPrompt } = useContext(AppContext);

  async function testCard(signup_info_data) {
    const createdPaymentMethod = await stripe.createPaymentMethod({
      type: 'card',
      card: signup_info_data?.card_number,
      billing_details: {
        address: {
          city: signup_info_data?.user?.city,
          country: 'US',
          line1: signup_info_data?.user?.address,
          line2: null,
          postal_code: signup_info_data?.user?.zip_code,
          state: signup_info_data?.user?.state
        },
        name: signup_info_data?.user?.first_name + ' ' + signup_info_data?.user?.last_name,
      },
    });
    return createdPaymentMethod;
  }
  const validation = useFormik({
    enableReinitialize: false,
    initialValues: {
      card_number: '',
      card_expiry: '',
      card_cvv: '',
      initial_deposit: 250,
      promo_code: '',
      accept_terms_of_service: false
    },
    validationSchema: Yup.object(
      use_new_stripe_setup ? {
        card_number: Yup.string().notRequired(),
        card_expiry: Yup.string().notRequired(),
        card_cvv: Yup.string().notRequired(),
      } : {
      card_number: Yup.string()
        .required(CONSTANTS.STR_REQUIRED_FIELD)
        .min(14),
      card_expiry: Yup.string()
        .required(CONSTANTS.STR_REQUIRED_FIELD)
        .min(3)
        .max(4),
      card_cvv: Yup.string()
        .required(CONSTANTS.STR_REQUIRED_FIELD)
        .min(3),

    }),
    onSubmit: async (values) => {
      const signup_info_data = {};
      signup_info_data.user = user_info;
      signup_info_data.campaigns = campaigns_to_be_created;
      signup_info_data.accept_terms_of_service = values.accept_terms_of_service
      signup_info_data.is_admin_adding_client = is_admin_adding_client
      signup_info_data.send_create_password_email = user_info?.send_create_password_email
      signup_info_data.card_number = use_new_stripe_setup ? elements.getElement(CardNumberElement) : values.card_number;
      signup_info_data.card_cvv = values.card_cvv;
      signup_info_data.card_expiry_month = values.card_expiry.toString().substring(0, 2);
      signup_info_data.card_expiry_year = `20${values.card_expiry.toString().substring(2, 4)}`;
      signup_info_data.initial_deposit = values.initial_deposit;
      if (urlParams.get('sid') !== null) {
        signup_info_data.sid = urlParams.get('sid');
      }

      if (use_new_stripe_setup) {
        const createdPaymentMethod = await testCard(signup_info_data);
        if (createdPaymentMethod?.error) {
          dispatch(registerUserFailed(createdPaymentMethod.error.message));
          return;
        }
        signup_info_data.payment_method_id = createdPaymentMethod?.paymentMethod?.id;
      }
      if (registration_page_enhancements) {
        signup_info_data.registration_pending_user_id = registration_pending_user?.id
      }
      dispatch(registerUser(signup_info_data, props.history, use_new_stripe_setup));
    },
  });

  const {
    payment_methods, loading, user, updating_card, user_info,
    registrationError, campaigns_to_be_created, registration_pending_user
  } = useSelector(
    (state) => ({
      payment_methods: state.PaymentMethods.payment_methods,
      loading: state.Account.loading,
      user: state.Account.user,
      updating_card: state.PaymentMethods.updating_card,
      user_info: state.Account.set_user_to_be_created,
      registrationError: state.Account.registrationError,
      campaigns_to_be_created: state.Account.campaigns_to_be_created,
      registration_pending_user: state.Account.registration_pending_user
    }),
    shallowEqual,
  );
  const location = useLocation();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);

  const redirectBackToRegister = () => {
    if (urlParams.get('invoice_user') !== null) {
      history.push("/register?invoice_user=" + urlParams.get('invoice_user') + registration_pending_user_url_param)
    } else if (urlParams.get('inv_sid') !== null) {
      history.push("/register?inv_sid=" + urlParams.get('inv_sid') + admin_url_param + registration_pending_user_url_param)
    } else if (urlParams.get('sid') !== null) {
      history.push("/register?sid=" + urlParams.get('sid') + admin_url_param + registration_pending_user_url_param)
    } else {
      history.push("/register" + registration_pending_user_url_param.replace(/&/, '?'))
    }
  }


  useEffect(() => {
    if (registrationError && registrationError === EMAIL_HAS_ALREADY_BEEN_TAKEN) {
      setShowExitPrompt(false)
      setIsUpdating(false)
      setTimeout(() => {
        redirectBackToRegister();
      }, 1000);
    }
  }, [registrationError]);

  useEffect(() => {
    setShowExitPrompt(!!user_info && isUpdating);
  }, [user_info, isUpdating]);

  const [locationKeys, setLocationKeys] = useState([]);

  const onSetUserInfoTimeout = () => {
    setCheckUserInfo(false);
    setTimeout(() => {
      setCheckUserInfo(true);
    }, 1000);
  };

  useEffect(() => {
    //Check if user_info exists, it is a fix for situations when a user directy enters url
    onSetUserInfoTimeout();
  }, []);

  useEffect(() => {
    if (checkUserInfo === true && (!user_info)) {
      ToastAlert.fire({ icon: ToastAlertIconType.ERROR, title: 'Please enter user info first' });
      redirectBackToRegister();
    }
  }, [checkUserInfo]);

  const state_names = [];
  us_states.map((e) => {
    state_names.push({ label: e.isocode, value: e.name });
  });
  const optionGroupStates = [
    {
      label: 'Select State',
      options: state_names,
    },
  ];
  let cardNumber = validation?.values?.card_number
  const cardDisplayImage = (cardNumber) => {
    for (const creditCard of cardDigits) {
      const creditCardNumbers = creditCard?.numbers;
      for (const num of creditCardNumbers) {
        if (cardNumber.startsWith(num)) {
          return creditCard?.image;
        }
      }
    }
    return creditCardImage;
  }
  useEffect(() => {
    const cardNumOfImage = cardDisplayImage(cardNumber);
    setSelectImageCreditCard(cardNumOfImage);
  }, [cardNumber])

  //Terms of service element handled by feature flag
  const TERMS_OF_SERVICE_HTML_ELEMENT = registration_page_enhancements ? (!is_admin_adding_client &&
    <div className='mt-4 text-center'>
      <p className="mb-0">
        <Input
          name='accept_terms_of_service'
          id='accept_terms_of_service'
          className='me-2 form-check-input'
          type='checkbox'
          value={validation.values.accept_terms_of_service}
          checked={validation.values.accept_terms_of_service}
          onChange={validation.handleChange}
        />
        I agree to the{" "}
        <a href={`${AppConfiguration.BACKEND_URL}/api/v1/terms_of_services/${LATEST}/download`} className="text-sfblue" target="_blank">
          Terms of Use
        </a>
      </p>
    </div>
  ) : (
    <div className="mt-4 text-center">
      <p className="mb-0">
        By registering you agree to the{" "}
        <a href={`${AppConfiguration.BACKEND_URL}/api/v1/terms_of_services/${LATEST}/download`} className="text-sfblue" target="_blank">
          Terms of Use
        </a>
      </p>
    </div>
  )

  return (
    <>
      <Prompt
        when={!!user_info && isUpdating}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <MetaTags>
        <title>Register Now | {AppConfiguration.WEBSITE}</title>
      </MetaTags>
      <div className="account-pages my-1 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={6}>
              <Card className="overflow-hidden">
                <div className="bg-white bg-soft">
                  <Row>
                    <Col xs={12}>
                      <div className="text-center p-4">
                        <img src={animatedlogo} height={32} />
                      </div>
                      <div className="text-dark px-4">
                        <h5 className="text-dark b">Create an account</h5>
                        <p>
                          Create your
                          {' '}
                          {AppConfiguration.WEBSITE}
                          {' '}
                          Ad Center account below or
                          if you need assistance contact us at {AppConfiguration.WHITE_LABELING_CONTACT_PHONE}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <Form
                    className="form-horizontal"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    {registrationError
                      && <Alert color="danger" className='sf-text-capitalize'>{registrationError}</Alert>}
                    <div className="">
                      {/* Billing information starts */}
                      <div className="mx-2 mt-2 mb-2">
                        <Label className={`label-title font-weight-semibold ${getPrimaryTextClass()}`}>
                          Billing Information
                        </Label>
                      </div>
                      <div className="mx-2">
                        <Row id='billing-registration-stripe'>
                          <Col lg="12">
                          </Col>
                          <Col lg="6">
                            <FormGroup className="mb-0">
                              <Label className="font-weight-semibold">
                                Credit Card
                              </Label>
                              { use_new_stripe_setup ? (
                                <CardNumberElement
                                  classname="form-control"
                                  options={{
                                    showIcon: true,
                                    iconStyle: 'solid',
                                    placeholder: '',
                                  }}
                                  onChange={(e) => {
                                    if(e.complete)
                                    {
                                      setCardNumberValid(true)
                                    }else
                                    {
                                      setCardNumberValid(false)
                                    }
                                  }}
                                />
                              ) : (
                              <div className="d-flex" id="billing-credit-card-image">
                                <img src={selectImageCreditCard} className="me-1 card-image" />
                                <NumberFormat
                                  name="card_number"
                                  format="####-####-####-####"
                                  type="text"
                                  className={validation.touched.card_number && validation.errors.card_number ? 'is-invalid form-control' : 'form-control'}
                                  onValueChange={(values) => {
                                    const { value } = values;
                                    validation.setFieldValue('card_number', value);
                                  }}
                                  id="card-input"
                                  onBlur={validation.handleBlur}
                                  value={validation.values.card_number || ''}
                                  invalid={validation.touched.card_number && validation.errors.card_number ? 'true' : 'false'}
                                  aria-invalid={validation.touched.card_number && validation.errors.card_number ? 'true' : 'false'}
                                />
                              </div>
                              )}

                              {validation.touched.card_number
                                && validation.errors.card_number ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.card_number}
                                </FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col lg="3">
                            <FormGroup className="mb-0">
                              <Label className="font-weight-semibold">
                                Expiry date
                              </Label>
                              { use_new_stripe_setup ? (
                                <CardExpiryElement
                                  classname="form-control"
                                  options={{
                                    placeholder: '',
                                  }}
                                  onChange={(e) => {
                                    if(e.complete)
                                    {
                                      setCardExpiryValid(true)
                                    }else
                                    {
                                      setCardExpiryValid(false)
                                    }
                                  }}
                                  />
                              ) : (
                              <NumberFormat
                                name="card_expiry"
                                format="##/##"
                                type="text"
                                mask="_"
                                className={validation.touched.card_expiry && validation.errors.card_expiry ? 'is-invalid form-control' : 'form-control'}
                                onValueChange={(values) => {
                                  const { value } = values;
                                  validation.setFieldValue('card_expiry', value);
                                }}
                                onBlur={validation.handleBlur}
                                value={validation.values.card_expiry || ''}
                                invalid={validation.touched.card_expiry && validation.errors.card_expiry ? 'true' : 'false'}
                                aria-invalid={validation.touched.card_expiry && validation.errors.card_expiry ? 'true' : 'false'}
                              />
                              )}
                              {validation.touched.card_expiry
                                && validation.errors.card_expiry ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.card_expiry}
                                </FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col lg="3">
                            <FormGroup className="mb-0">
                              <Label className="font-weight-semibold">
                                CVV Code
                              </Label>
                              {use_new_stripe_setup ? (
                                <CardCvcElement
                                  classname="form-control"
                                  options={{
                                    placeholder: '',
                                  }}
                                  onChange={(e) => {
                                    if(e.complete)
                                    {
                                      setCardCVVValid(true)
                                    } else
                                    {
                                      setCardCVVValid(false)
                                    }
                                  }}
                                  />
                              ) : (
                              <NumberFormat
                                name="card_cvv"
                                format="####"
                                type="text"
                                className={validation.touched.card_cvv && validation.errors.card_cvv ? 'is-invalid form-control' : 'form-control'}
                                onValueChange={(values) => {
                                  const { value } = values;
                                  validation.setFieldValue('card_cvv', value);
                                }}
                                onBlur={validation.handleBlur}
                                value={validation.values.card_cvv || ''}
                                invalid={validation.touched.card_cvv && validation.errors.card_cvv ? 'true' : 'false'}
                                aria-invalid={validation.touched.card_cvv && validation.errors.card_cvv ? 'true' : 'false'}
                              />)}
                              {validation.touched.card_cvv
                                && validation.errors.card_cvv ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.card_cvv}
                                </FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          {isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_ENABLE_CARD_CONVENIENCE_FEE) && 
                            <div className="mt-0 mb-2 small">
                              {convenienceFeeNote()}
                            </div>}
                          </Row>
                      </div>
                      {/* Billing Information ends */}
                      {/* Billing Address starts */}
                      <div className="mx-2 mt-3 mb-2">
                        <Row>
                          <Col lg="6">
                            <FormGroup className="mb-0">
                              <Label className="font-weight-semibold">
                                Initial Deposit Amount
                              </Label>
                              {/* <Select
                                                value={selectedStatusGroup}
                                                onChange={setSelectedStatusGroup}
                                                options={optionGroupStatus}
                                                classNamePrefix="select2-selection"
                                            /> */}
                              <div className="styled-select select">
                                <select
                                  value={selectedCreditAmount.value}
                                  onChange={(selectedCreditOption) => {
                                    setSelectedCreditAmount({
                                      label: 'Label',
                                      value: selectedCreditOption.target.value,
                                    });
                                    validation.setFieldValue('initial_deposit', selectedCreditOption.target.value);
                                  }}
                                >
                                  {add_funds_options_billing.map((e) => (
                                    <option
                                      key={e.value}
                                      value={e.value}
                                    >
                                      {SfDollarStringFormatter(e.label)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup className="mb-0">
                              <Label className="font-weight-semibold">
                                Promo Code
                              </Label>
                              <Input
                                type="text"
                                name="promo_code"
                                className="form-control"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                defaultValue={validation.values.promo_code || ''}
                                invalid={
                                  !!(validation.touched.promo_code
                                    && validation.errors.promo_code)
                                }
                              />
                              {validation.touched.promo_code
                                && validation.errors.promo_code ? (
                                <FormFeedback type="invalid">
                                  {validation.errors.promo_code}
                                </FormFeedback>
                              ) : null}
                            </FormGroup>
                          </Col>
                          <Col>
                            {/* <div className="form-check mt-2">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="customControlInline"
                                name="terms"
                              />
                              <label
                                className="form-check-label"
                                htmlFor="customControlInline"
                              >
                                I have read and agreed to the
                                {' '}
                                <span className="text-sfblue">Terms and Conditions</span>
                              </label>
                            </div> */}
                            {/* Checkbox for accepting terms of service */}
                            {TERMS_OF_SERVICE_HTML_ELEMENT}
                          </Col>
                          <Col lg="12">
                            <div className="mt-4 mb-4 d-grid">
                              <button
                                className={`btn ${getPrimaryButtonClass()} btn-block`}
                                type="submit"
                                disabled = {(registration_page_enhancements && !is_admin_adding_client && !validation.values.accept_terms_of_service) || (use_new_stripe_setup && !(cardCVVValid && cardExpiryValid && cardNumberValid))}
                              >
                                {(loading)
                                  ? 'Please wait...' : 'Create an account'}
                              </button>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </Form>
                </CardBody>
              </Card>
              <div className="mt-5 text-white text-center">
                <p>
                  Already have an account ?
                  {' '}
                  <Link to="/login" className="font-weight-medium text-sfblue">
                    {' '}
                    Login
                  </Link>
                  {' '}
                </p>
                <p>
                  ©
                  {new Date().getFullYear()}
                  {' '}
                  {AppConfiguration.WEBSITE}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

BillingRegistration.propTypes = {
  options: PropTypes.object,
  clientSecret: PropTypes.string,
  onCancel: PropTypes.func,
};

export default BillingRegistration;
